import { useState, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { StatCard } from '@/shared/ui/StatCard'
import { DateRangePicker, defaultDateRange } from '@/shared/ui/DateRangePicker'
import { DataTable } from '@/shared/ui/DataTable'
import { Badge } from '@/shared/ui/Badge'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { TrendingUp, Users, School, Star, Calendar, GraduationCap, Building2, FileDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import jsPDF from 'jspdf'
import { useBookingReport, useRevenueReport, useTrainerReport, useStudentReport, useSchoolReport } from '@/api/hooks'
import { usePagination } from '@/shared/hooks'
import { toast } from '@/store/useToastStore'
import { formatCurrency } from '@/shared/utils'
import { schoolService } from '@/api/services/schoolService'
import { getApiError } from '@/api/client'
import {
  reportService,
  type ReportType,
  type BookingReportRow, type RevenueReportRow, type RevenueReportTrainerRow,
  type TrainerReportRow, type StudentReportRow, type SchoolReportRow,
} from '@/api/services/reportService'

type ReportKey = 'bookings' | 'revenue' | 'trainer' | 'student' | 'school'

interface TabMeta {
  label: string; path: string; key: ReportKey; type: ReportType
  icon: LucideIcon; color: 'blue' | 'green' | 'amber' | 'purple'
  description: string
}

const TABS: TabMeta[] = [
  { label:'Booking Report',  path:'/dashboard/report/bookings', key:'bookings', type:'booking',
    icon: Calendar, color:'blue', description: 'Bookings by school — totals, session outcomes, active trainers and students.' },
  { label:'Revenue Report',  path:'/dashboard/report/revenue',  key:'revenue',  type:'revenue',
    icon: TrendingUp, color:'green', description: 'Revenue collected per school and how it breaks down across individual trainers.' },
  { label:'Trainer Report',  path:'/dashboard/report/trainer',  key:'trainer',  type:'trainer',
    icon: Users, color:'amber', description: 'Trainer performance — bookings, sessions, cancellation rate and average rating.' },
  { label:'Student Report',  path:'/dashboard/report/student',  key:'student',  type:'student',
    icon: GraduationCap, color:'blue', description: 'Student progress — sessions completed, amount paid and certification status.' },
  { label:'School Report',   path:'/dashboard/report/school',   key:'school',   type:'school',
    icon: Building2, color:'purple', description: 'A directory of schools with admin contact details and trainer headcount.' },
]

const HEADER_BAR: Record<TabMeta['color'], string> = {
  blue: 'bg-brand-500', green: 'bg-green-500', amber: 'bg-brand-500', purple: 'bg-purple-500',
}

const enName = (arr: any[]) => arr?.find((item: any) => item?.locale?.toLowerCase() === 'en')?.name ?? arr?.[0]?.name

// Print-only layout for the trainer revenue PDF — plain inline styles (not Tailwind) so
// html2canvas renders it exactly as intended, independent of the app's CSS. Kept monochrome
// (black/white/gray) for a formal, document-like look rather than the app's colored UI chrome.
const th: CSSProperties = { padding: '8px 10px', fontSize: 10, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.3, textAlign: 'left', borderBottom: '2px solid #111827' }
const td: CSSProperties = { padding: '8px 10px', fontSize: 11, color: '#111827', borderBottom: '1px solid #e5e7eb' }

function TrainerRevenuePdfDocument({ school, rows, total }: {
  school: string; rows: RevenueReportTrainerRow[]; total: number
}) {
  const { t } = useTranslation()
  return (
    <div style={{ width: 700, padding: 28, fontFamily: 'Arial, Helvetica, sans-serif', background: '#fff', color: '#111827' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 14, marginBottom: 18, borderBottom: '2px solid #111827' }}>
        <img src="/logo.svg" alt="Drivys" style={{ height: 34, objectFit: 'contain' }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t('reportPage.pdf.title')}</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{t('reportPage.pdf.generated', { date: new Date().toLocaleString() })}</div>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18 }}>
        <tbody>
          <tr>
            <td style={{ padding: '2px 0', fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, width: '33%' }}>{t('reportPage.pdf.school')}</td>
            <td style={{ padding: '2px 0', fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, width: '33%' }}>{t('reportPage.pdf.trainers')}</td>
            <td style={{ padding: '2px 0', fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>{t('reportPage.pdf.totalRevenue')}</td>
          </tr>
          <tr>
            <td style={{ padding: '2px 0', fontSize: 13, fontWeight: 700 }}>{school || '—'}</td>
            <td style={{ padding: '2px 0', fontSize: 13, fontWeight: 700 }}>{rows.length}</td>
            <td style={{ padding: '2px 0', fontSize: 13, fontWeight: 700 }}>{formatCurrency(total)}</td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>{t('reportPage.pdf.trainerCol')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('reportPage.pdf.revenueCol')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('reportPage.pdf.bookingsCol')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((tr, i) => (
            <tr key={i}>
              <td style={td}>{tr['Trainer Name'] || '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{formatCurrency(tr.Revenue ?? 0)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{tr['Total Number Of Bookings']}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 18, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontSize: 9, color: '#9ca3af', textAlign: 'center' }}>
        {t('reportPage.pdf.footer')}
      </div>
    </div>
  )
}

export function ReportPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const activeTab = TABS.find(tb => location.pathname.includes(tb.key)) ?? TABS[0]
  const { key, type } = activeTab
  // Keep tab links within the area the user is in (school admins stay on /school/*).
  const isSchool = location.pathname.startsWith('/school')
  const tabs = isSchool
    ? TABS.map(tb => ({ ...tb, path: tb.path.replace('/dashboard/', '/school/') }))
    : TABS

  // Default to "This Month" so reports load with a sensible range instead of an unbounded query.
  const [{ from: dateFrom, to: dateTo }, setDateRange] = useState(defaultDateRange)
  const [schoolFilter, setSchool]   = useState('')
  const [trainerModal, setTrainerModal] = useState<{ school: string; rows: RevenueReportRow['Bookings Revenue By Trainer'] } | null>(null)
  const [exporting, setExporting]   = useState(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const trainerPdfRef = useRef<HTMLDivElement>(null)
  const trainerRevenueTotal = (trainerModal?.rows ?? []).reduce((sum, tr) => sum + (Number(tr.Revenue) || 0), 0)
  const { pagination, setPagination } = usePagination({ page: 1, per_page: 15 })

  const resetPage = (fn: (v: string) => void) => (v: string) => { fn(v); setPagination({ ...pagination, page: 1 }) }
  const handleDateRangeChange = (range: { from: string; to: string }) => {
    setDateRange(range)
    setPagination({ ...pagination, page: 1 })
  }

  const params = {
    start_date: dateFrom || undefined,
    end_date:   dateTo   || undefined,
    vendor_id:  !isSchool && schoolFilter ? Number(schoolFilter) : undefined,
    page:       pagination.page,
    limit:      pagination.per_page,
  }
  const scope = isSchool ? 'school' as const : 'admin' as const

  const bookingR = useBookingReport(key === 'bookings' ? params : undefined, { enabled: key === 'bookings', scope })
  const revenueR = useRevenueReport(key === 'revenue'  ? params : undefined, { enabled: key === 'revenue',  scope })
  const trainerR = useTrainerReport(key === 'trainer'  ? params : undefined, { enabled: key === 'trainer',  scope })
  const studentR = useStudentReport(key === 'student'  ? params : undefined, { enabled: key === 'student',  scope })
  const schoolR  = useSchoolReport( key === 'school'   ? params : undefined, { enabled: key === 'school',   scope })

  const q = { bookings: bookingR, revenue: revenueR, trainer: trainerR, student: studentR, school: schoolR }[key]
  const reportData: any = q.data
  const rows: any[]   = reportData?.data ?? []
  const total          = reportData?.total ?? 0
  const perPage        = reportData?.count_per_page ?? pagination.per_page
  const lastPage       = Math.ceil(total / perPage) || 1

  // School filter dropdown — only meaningful for the admin-wide view (school admins are already scoped server-side).
  const { data: schoolsData } = useQuery({
    queryKey: ['schools-filter-reports'],
    queryFn: () => schoolService.list({ limit: 500 }),
    staleTime: 60_000,
    enabled: !isSchool,
  })
  const schoolOptions = ((schoolsData as any)?.data ?? []).map((s: any) => ({
    value: String(s.id), label: enName(s.vendor_translations) ?? `School #${s.id}`,
  }))

  const handleExport = async () => {
    setExporting(true)
    try {
      const { blob, filename } = await reportService.downloadFile(type, params)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      // The anchor must be in the document for the click to trigger a download
      // in Firefox, and the object URL has to outlive the click — revoking it
      // synchronously cancels the save in Chrome.
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch (e: any) {
      toast.error(getApiError(e) || e?.message || t('reportPage.exportFailed'))
    } finally {
      setExporting(false)
    }
  }

  // jsPDF's native text renderer only supports Latin/WinAnsi glyphs — Arabic school/trainer
  // names come out as garbled characters. Instead, render the actual (browser-rendered, so
  // any script works) DOM node to the PDF via jsPDF's html2canvas-backed `.html()` method.
  // html2canvas computes its capture region from the element's real on-screen position, so an
  // off-screen clone (e.g. `left: -99999px`) captures blank — instead, temporarily remove the
  // scroll/height cap on the same visible table (see `generatingPdf` below) so it's fully
  // expanded and in-flow, capture it, then restore the capped scrollable view.
  const handleDownloadTrainerPdf = async () => {
    if (!trainerModal || !trainerPdfRef.current) return
    setGeneratingPdf(true)
    try {
      // Wait for the un-capped layout to actually commit and paint before capturing it.
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      const doc = new jsPDF({ unit: 'pt', format: 'a4' })
      await doc.html(trainerPdfRef.current, {
        margin: 24,
        autoPaging: 'text',
        width: 547, // A4 width (595pt) minus margins
        windowWidth: 700,
      })
      doc.save(`trainer-revenue-${(trainerModal.school || 'school').toLowerCase().replace(/\s+/g, '-')}.pdf`)
    } catch {
      toast.error(t('reportPage.pdfFailed'))
    } finally {
      setGeneratingPdf(false)
    }
  }

  // ── Per-report-type columns (field names match the API's own labels exactly) ──
  // The school column is dropped for the school-admin view below — every row is already
  // scoped to their own single school server-side, so repeating its name/ID is just noise.
  const bookingColumns: ColumnDef<BookingReportRow>[] = [
    { header: t('reportPage.columns.school'), id: 'school', cell: ({ row }) => (
      <div>
        <div className="font-semibold text-gray-900 text-sm">{row.original['School Name'] || '—'}</div>
        <div className="text-xs text-gray-400 font-mono">#{row.original['School ID']}</div>
      </div>
    )},
    { header: t('reportPage.columns.bookings'), id: 'bookings', cell: ({ row }) => (
      <div>
        <div className="font-bold text-gray-900 text-sm">{row.original['Total Number of Bookings']}</div>
        <div className="text-xs text-gray-400">{t('reportPage.badges.paidCount', { count: row.original['Total Paid Bookings'] })}</div>
      </div>
    )},
    { header: t('reportPage.columns.sessions'), id: 'sessions', cell: ({ row }) => {
      const r = row.original
      return (
        <div>
          <div className="font-bold text-gray-900 text-sm mb-1">{t('reportPage.badges.totalSessions', { count: r['Total Sessions'] })}</div>
          <div className="flex flex-wrap gap-1">
            <Badge color="green" className="text-[12px] px-1.5 py-0">{r['Completed Sessions']} {t('reportPage.badges.done')}</Badge>
            <Badge color="red" className="text-[12px] px-1.5 py-0">{r['Cancelled Sessions']} {t('reportPage.badges.cancelled')}</Badge>
            <Badge color="amber" className="text-[12px] px-1.5 py-0">{r['Pending Sessions']} {t('reportPage.badges.pending')}</Badge>
            <Badge color="blue" className="text-[12px] px-1.5 py-0">{r['Rescheduled Sessions']} {t('reportPage.badges.rescheduled')}</Badge>
          </div>
        </div>
      )
    }},
    { header: t('reportPage.columns.activeTrainers'), accessorKey: 'Active Trainers', cell: ({ getValue }) => <span className="text-sm font-semibold text-gray-800">{getValue<number>()}</span> },
    { header: t('reportPage.columns.students'), accessorKey: 'Total Students', cell: ({ getValue }) => <span className="text-sm font-semibold text-gray-800">{getValue<number>()}</span> },
  ]

  const revenueColumns: ColumnDef<RevenueReportRow>[] = [
    { header: t('reportPage.columns.school'), accessorKey: 'School Name', cell: ({ getValue }) => <span className="font-semibold text-gray-900 text-sm">{getValue<string>() || '—'}</span> },
    { header: t('reportPage.columns.bookings'), accessorKey: 'Total Number Of Bookings', cell: ({ getValue }) => <span className="font-bold text-gray-900 text-sm">{getValue<number>()}</span> },
    { header: t('reportPage.columns.revenueSchool'), accessorKey: 'Bookings Revenue By School', cell: ({ getValue }) => <span className="font-bold text-gray-900">{formatCurrency(getValue<number>() ?? 0)}</span> },
    { header: t('reportPage.columns.byTrainer'), id: 'trainers', cell: ({ row }) => {
      const trainers = row.original['Bookings Revenue By Trainer']
      const count = Array.isArray(trainers) ? trainers.length : 0
      if (!count) return <span className="text-xs text-gray-400">—</span>
      return (
        <button
          onClick={() => setTrainerModal({ school: row.original['School Name'], rows: trainers })}
          className="text-xs font-semibold text-brand-600 hover:underline"
        >
          {t('reportPage.viewTrainers', { count })}
        </button>
      )
    }},
  ]

  const trainerColumns: ColumnDef<TrainerReportRow>[] = [
    { header: t('reportPage.columns.trainer'), id: 'trainer', cell: ({ row }) => (
      <div>
        <div className="font-semibold text-gray-900 text-sm">{row.original['Trainer Name'] || '—'}</div>
        <div className="text-xs text-gray-400 font-mono">#{row.original['Trainer ID']}</div>
      </div>
    )},
    { header: t('reportPage.columns.school'), accessorKey: 'School Name', cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue<string>() || '—'}</span> },
    { header: t('reportPage.columns.bookings'), accessorKey: 'Total Bookings', cell: ({ getValue }) => <span className="font-bold text-gray-900 text-sm">{getValue<number>()}</span> },
    { header: t('reportPage.columns.sessions'), accessorKey: 'Total Sessions', cell: ({ getValue }) => <span className="text-sm font-semibold text-gray-800">{getValue<number>()}</span> },
    { header: t('reportPage.columns.cancellationRate'), accessorKey: 'Cancellation Rate', cell: ({ getValue }) => {
      const raw = getValue<string>() ?? '0%'
      const pct = parseFloat(raw)
      const color = pct >= 70 ? 'red' : pct >= 30 ? 'amber' : 'green'
      return <Badge color={color}>{raw}</Badge>
    }},
    { header: t('reportPage.columns.rating'), accessorKey: 'Average Rating', cell: ({ getValue }) => {
      const raw = getValue<number | string>()
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw))
      return (!isNaN(num) && num > 0) ? (
        <span className="flex items-center gap-1 text-sm font-semibold text-gray-800">
          <Star size={12} className="text-brand-400 fill-brand-400"/>{num.toFixed(1)}
        </span>
      ) : <span className="text-xs text-gray-400">—</span>
    }},
  ]

  const studentColumns: ColumnDef<StudentReportRow>[] = [
    { header: t('reportPage.columns.student'), id: 'student', cell: ({ row }) => (
      <div>
        <div className="font-semibold text-gray-900 text-sm">{row.original['Student Name'] || '—'}</div>
        <div className="text-xs text-gray-400 font-mono">#{row.original['Student ID']}</div>
      </div>
    )},
    { header: t('reportPage.columns.category'), accessorKey: 'Category', cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue<string>() || '—'}</span> },
    { header: t('reportPage.columns.sessions'), accessorKey: 'Total Sessions', cell: ({ getValue }) => <span className="font-bold text-gray-900 text-sm">{getValue<number>()}</span> },
    { header: t('reportPage.columns.amountPaid'), accessorKey: 'Amount Paid', cell: ({ getValue }) => <span className="font-bold text-gray-900">{formatCurrency(getValue<number>() ?? 0)}</span> },
    { header: t('reportPage.columns.certificate'), accessorKey: 'Certificate Issued', cell: ({ getValue }) => {
      const v = getValue<string>()
      const label = v === 'Yes' ? t('common.yes') : v === 'No' ? t('common.no') : (v ?? '—')
      return <Badge color={v === 'Yes' ? 'green' : 'gray'}>{label}</Badge>
    }},
  ]

  const schoolColumns: ColumnDef<SchoolReportRow>[] = [
    { header: t('reportPage.columns.school'), id: 'school', cell: ({ row }) => (
      <div>
        <div className="font-semibold text-gray-900 text-sm">{row.original['School Name'] || '—'}</div>
        <div className="text-xs text-gray-400 font-mono">#{row.original['School ID']}</div>
      </div>
    )},
    { header: t('reportPage.columns.adminEmail'), accessorKey: 'School Admin Email', cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue<string>() || '—'}</span> },
    { header: t('reportPage.columns.adminPhone'), accessorKey: 'School Admin Phone Number', cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue<string>() || '—'}</span> },
    { header: t('reportPage.columns.trainers'), accessorKey: 'Trainers Count', cell: ({ getValue }) => <span className="text-sm font-bold text-gray-900">{getValue<number>() ?? 0}</span> },
  ]

  const columns = (
    {
      bookings: bookingColumns, revenue: revenueColumns, trainer: trainerColumns, student: studentColumns, school: schoolColumns,
    }[key] as ColumnDef<any>[]
  // School admins are already scoped to their own single school server-side — drop the
  // redundant School column from the tabs that have one (the School Report tab keeps it,
  // since that tab's whole purpose is listing schools).
  ).filter(c => !(isSchool && key !== 'school' && c.header === t('reportPage.columns.school')))

  // Stat cards use only fields the API actually returns — no fabricated aggregates from a partial page.
  // The school-scoped revenue report has no `total_bookings_revenue` (admin-only, a global sum) — fall
  // back to the single school's own "Bookings Revenue By School" for that view.
  const revenueTotal = reportData?.total_bookings_revenue ?? (isSchool ? rows[0]?.['Bookings Revenue By School'] : undefined) ?? 0
  const statCards = {
    bookings: [{ title: t('reportPage.stats.schoolsReporting'), value: total, icon: <School size={18}/>, color: 'blue' as const }],
    revenue: isSchool
      ? [{ title: t('reportPage.stats.bookingsRevenue'), value: formatCurrency(revenueTotal), icon: <TrendingUp size={18}/>, color: 'green' as const }]
      : [
          { title: t('reportPage.stats.totalBookingsRevenue'), value: formatCurrency(revenueTotal), icon: <TrendingUp size={18}/>, color: 'green' as const },
          { title: t('reportPage.stats.schoolsReporting'), value: total, icon: <School size={18}/>, color: 'purple' as const },
        ],
    trainer: [{ title: t('reportPage.stats.totalTrainers'), value: total, icon: <Users size={18}/>, color: 'amber' as const }],
    student: [{ title: t('reportPage.stats.totalStudents'), value: total, icon: <Users size={18}/>, color: 'blue' as const }],
    school: [{ title: t('reportPage.stats.totalSchools'), value: total, icon: <School size={18}/>, color: 'purple' as const }],
  }[key]

  const ActiveIcon = activeTab.icon

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('reportPage.pageTitle')}
        subtitle={t(`reportPage.tabs.${key}.description`)}
        breadcrumbs={[{label:t('reportPage.breadcrumb.dashboard'),to:isSchool?'/school/overview':'/dashboard'},{label:t('reportPage.breadcrumb.reports')}]}
      />

      <div className="flex gap-1 border-b border-gray-100 bg-white rounded-2xl px-2 pt-2 overflow-x-auto shadow-sm">
        {tabs.map(tb => {
          const Icon = tb.icon
          return (
            <NavLink key={tb.path} to={tb.path}
              className={({isActive})=>`flex items-center gap-1.5 px-4 py-2.5 rounded-t-xl text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive?'border-brand-500 text-brand-600 bg-brand-50/60':'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
              <Icon size={14}/>{t(`reportPage.tabs.${tb.key}.label`)}
            </NavLink>
          )
        })}
      </div>

      <Card className="rounded-2xl">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <DateRangePicker from={dateFrom} to={dateTo} onChange={handleDateRangeChange} />
          {!isSchool && (
            <select
              value={schoolFilter}
              onChange={e => resetPage(setSchool)(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white min-w-32"
            >
              <option value="">{t('reportPage.schoolFilterPlaceholder')}</option>
              {schoolOptions.map((o: { value: string; label: string }) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          {!isSchool && (
            <Button variant="outline" size="sm" icon={<FileDown size={13} />} onClick={handleExport} loading={exporting} className="ms-auto">
              {t('reportPage.export')}
            </Button>
          )}
        </div>
      </Card>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${statCards.length > 2 ? 'md:grid-cols-4' : ''} gap-4`}>
        {statCards.map(s => <StatCard key={s.title} title={s.title} value={s.value} icon={s.icon} color={s.color}/>)}
      </div>

      <Card padding={false} className="rounded-2xl overflow-hidden">
        <div className={`px-5 py-3 flex items-center justify-between gap-3 ${HEADER_BAR[activeTab.color]}`}>
          <span className="flex items-center gap-2 text-sm font-bold text-white"><ActiveIcon size={15}/>{t(`reportPage.tabs.${key}.label`)}</span>
          <span className="text-xs font-medium text-white/80">{t('reportPage.resultsCount', { count: total })}</span>
        </div>
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">{t('reportPage.loadFailed')}</p>}
        <DataTable
          columns={columns}
          data={rows}
          isLoading={q.isLoading}
          emptyMessage={t(`reportPage.noData.${key}`)}
          pagination={{ page: pagination.page, per_page: pagination.per_page, total, last_page: lastPage }}
          onPaginationChange={setPagination}
        />
      </Card>

      <Modal open={!!trainerModal} onClose={() => setTrainerModal(null)} title={t('reportPage.trainerRevenueModalTitle', { school: trainerModal?.school ?? '' })} size="lg">
        {/* Summary + actions row */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{t('reportPage.trainersCount', { count: trainerModal?.rows?.length ?? 0 })}</span>
            <span className="w-px h-4 bg-gray-200"/>
            <span>{t('reportPage.totalRevenueLabel', { amount: formatCurrency(trainerRevenueTotal) })}</span>
          </div>
          <Button variant="outline" size="sm" icon={<FileDown size={13}/>} onClick={handleDownloadTrainerPdf} loading={generatingPdf}>
            {t('reportPage.downloadPdf')}
          </Button>
        </div>

        {/* Same element serves both the on-screen popup and the PDF capture source — html2canvas
            captures based on real on-screen position, so an off-screen/hidden clone captures
            blank. While generating, this swaps in a branded print layout in the exact same spot
            (briefly visible, matching the button's loading state), then restores the normal
            compact scrollable list once the PDF is saved. */}
        <div ref={trainerPdfRef} className="bg-white">
          {generatingPdf ? (
            <TrainerRevenuePdfDocument
              school={trainerModal?.school || ''}
              rows={trainerModal?.rows ?? []}
              total={trainerRevenueTotal}
            />
          ) : (
            <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-gray-100">
              <table className="drivys-table w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr><th>{t('reportPage.columns.trainer')}</th><th>{t('reportPage.columns.revenue')}</th><th>{t('reportPage.columns.bookings')}</th></tr>
                </thead>
                <tbody>
                  {(trainerModal?.rows ?? []).map((tr, i) => (
                    <tr key={i}>
                      <td className="font-medium text-gray-900 text-sm">{tr['Trainer Name'] || '—'}</td>
                      <td className="font-bold text-gray-900 text-sm">{formatCurrency(tr.Revenue ?? 0)}</td>
                      <td className="text-sm text-gray-700">{tr['Total Number Of Bookings']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}
